// Device Configuration for Ticket Window Interface
// This file contains the configuration for all available Bluetooth devices

// Master list of supported locations with popular destinations
export const LOCATIONS = [
    {
        name: "Bristol Temple Meads",
        crs: "BRI",
        geohash: "gcnhtq07",
        popularDestinations: {
            rail: [
                { name: "London Paddington", stopCode: "PAD", stopCodeType: "CRS" },
                { name: "Bath Spa", stopCode: "BTH", stopCodeType: "CRS" },
                { name: "Cardiff Central", stopCode: "CDF", stopCodeType: "CRS" },
                { name: "Taunton", stopCode: "TAU", stopCodeType: "CRS" },
                { name: "Swindon", stopCode: "SWI", stopCodeType: "CRS" },
                { name: "Bristol Parkway", stopCode: "BPW", stopCodeType: "CRS" }
            ],
            bus: []
        }
    },
    {
        name: "Reading",
        crs: "RDG",
        geohash: "gcpk9zsj", 
        popularDestinations: {
            rail: [
                { name: "London Paddington", stopCode: "PAD", stopCodeType: "CRS" },
                { name: "Oxford", stopCode: "OXF", stopCodeType: "CRS" },
                { name: "Bristol Temple Meads", stopCode: "BRI", stopCodeType: "CRS" },
                { name: "Birmingham New Street", stopCode: "BHM", stopCodeType: "CRS" },
                { name: "Manchester Piccadilly", stopCode: "MAN", stopCodeType: "CRS" },
                { name: "Slough", stopCode: "SLO", stopCodeType: "CRS" }
            ],
            bus: []
        }
    }
];

export const DEVICES = [
    {
        id: "00030",
        name: "Bristol Temple Meads - Ticket Office",
        location: "Bristol Temple Meads",
        locationCrs: "BRI",
        bluetoothName: "TicketWindow_00030",
        deviceType: "Ticket_Window_Device",
        manufacturer: "Journey Alerts Ltd",
        version: "1.0.0",
        bluetooth: {
            serviceUuid: "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
            txCharUuid: "6e400003-b5a3-f393-e0a9-e50e24dcca9e", // TX (device to client)
            rxCharUuid: "6e400002-b5a3-f393-e0a9-e50e24dcca9e", // RX (client to device)
            maxMessageSize: 512
        }
    },
    {
        id: "00031",
        name: "Reading - Ticket Office",
        location: "Reading",
        locationCrs: "RDG",
        bluetoothName: "TicketWindow_00031",
        deviceType: "Ticket_Window_Device",
        manufacturer: "Journey Alerts Ltd",
        version: "1.0.0",
        bluetooth: {
            serviceUuid: "7e400001-b5a3-f393-e0a9-e50e24dcca9e",
            txCharUuid: "7e400003-b5a3-f393-e0a9-e50e24dcca9e", // TX (device to client)
            rxCharUuid: "7e400002-b5a3-f393-e0a9-e50e24dcca9e", // RX (client to device)
            maxMessageSize: 512
        }
    }
];

// Get device by ID
export const getDeviceById = (deviceId) => {
    return DEVICES.find(device => device.id === deviceId);
};

// Get device by Bluetooth name
export const getDeviceByBluetoothName = (bluetoothName) => {
    return DEVICES.find(device => device.bluetoothName === bluetoothName);
};

// Default device (first device in config)
export const DEFAULT_DEVICE = DEVICES[0];

// Get location by name
export const getLocationByName = (locationName) => {
    return LOCATIONS.find(location => location.name === locationName);
};

// Get all location names (sorted)
export const getDistinctLocations = () => {
    return LOCATIONS.map(location => location.name).sort();
};

// Get devices by location
export const getDevicesByLocation = (location) => {
    return DEVICES.filter(device => device.location === location);
};

// Check if a device is in standalone mode
export const isStandaloneMode = (deviceId) => {
    return deviceId === "standalone";
};

// Check if Web Bluetooth is supported
export const isBluetoothSupported = () => {
    return 'bluetooth' in navigator;
};

// Bluetooth connection status
export const BLUETOOTH_STATUS = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting', 
    CONNECTED: 'connected',
    ERROR: 'error'
};

// Favourites Management
const FAVOURITES_STORAGE_KEY = 'journey_favourites';

// Get favourites for a location from localStorage
export const getFavourites = (locationName) => {
    try {
        const stored = localStorage.getItem(FAVOURITES_STORAGE_KEY);
        const allFavourites = stored ? JSON.parse(stored) : {};
        
        if (!allFavourites[locationName]) {
            // Initialize with default popular destinations
            const location = getLocationByName(locationName);
            if (location?.popularDestinations) {
                allFavourites[locationName] = {
                    rail: location.popularDestinations.rail.map(dest => ({
                        ...dest,
                        searchCount: 0
                    })),
                    bus: location.popularDestinations.bus.map(dest => ({
                        ...dest,
                        searchCount: 0
                    }))
                };
                localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(allFavourites));
            } else {
                allFavourites[locationName] = { rail: [], bus: [] };
            }
        }
        
        return allFavourites[locationName];
    } catch (error) {
        console.error('Error loading favourites:', error);
        return { rail: [], bus: [] };
    }
};

// Add or update a destination in favourites
export const updateFavourite = (locationName, destination, mode) => {
    try {
        const stored = localStorage.getItem(FAVOURITES_STORAGE_KEY);
        const allFavourites = stored ? JSON.parse(stored) : {};
        
        if (!allFavourites[locationName]) {
            allFavourites[locationName] = { rail: [], bus: [] };
        }
        
        const modeKey = mode.toLowerCase();
        const favouritesList = allFavourites[locationName][modeKey] || [];
        
        // Find existing favourite
        const existingIndex = favouritesList.findIndex(fav => 
            fav.name === destination.name && fav.stopCode === destination.stopCode
        );
        
        if (existingIndex >= 0) {
            // Increment search count
            favouritesList[existingIndex].searchCount++;
        } else {
            // Add new favourite
            favouritesList.push({
                ...destination,
                searchCount: 1
            });
        }
        
        // Sort by search count (descending) and limit to top 6 for rail, 4 for bus
        const maxItems = modeKey === 'rail' ? 6 : 4;
        allFavourites[locationName][modeKey] = favouritesList
            .sort((a, b) => b.searchCount - a.searchCount)
            .slice(0, maxItems);
        
        localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(allFavourites));
        
        return allFavourites[locationName];
    } catch (error) {
        console.error('Error updating favourite:', error);
        return getFavourites(locationName);
    }
};

// Get sorted favourites for display
export const getSortedFavourites = (locationName) => {
    const favourites = getFavourites(locationName);
    return {
        rail: favourites.rail
            .sort((a, b) => b.searchCount - a.searchCount)
            .slice(0, 6),
        bus: favourites.bus
            .sort((a, b) => b.searchCount - a.searchCount)
            .slice(0, 4)
    };
};
